import * as React from 'react';

import { Button, Menu } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  CheckSquare,
  Download,
  Edit,
  FileType2,
  MoreHorizontal,
  Move,
  Trash2,
} from 'lucide-react';

import {
  $selectedFileForAction,
  $showDeleteDialog,
  $showMoveDialog,
  $showRenameDialog,
  $templateDialogFileId,
} from '../../_state/fileDialogs.state';
import { $generateAccessUrl } from '../../_state/fileGenerateAccessUrl.state';
import {
  $isSelectionMode,
  $toggleFileSelection,
  $toggleSelectionMode,
} from '../../_state/files.state';
import { useTranslations } from '../../i18n/useTranslations';
import { FileRecord } from '../../types/files';
import {
  canRenameFile,
  canMoveFile,
  canDeleteFile,
} from '../../utils/fileMetadata';

interface FileActionMenuProps {
  file: FileRecord;
}

export const FileActionMenu: React.FC<FileActionMenuProps> = ({ file }) => {
  const t = useTranslations();
  const toast = useToast();
  const setShowRename = useSetAtom($showRenameDialog);
  const setShowMove = useSetAtom($showMoveDialog);
  const setShowDelete = useSetAtom($showDeleteDialog);
  const setTemplateDialogFileId = useSetAtom($templateDialogFileId);
  const setSelectedFile = useSetAtom($selectedFileForAction);
  const generateAccessUrl = useSetAtom($generateAccessUrl);
  const isSelectionMode = useAtomValue($isSelectionMode);
  const toggleSelectionMode = useSetAtom($toggleSelectionMode);
  const toggleFileSelection = useSetAtom($toggleFileSelection);

  const isFolder = file.type === 'Directory';
  const isOfficeDoc = file.name.match(/\.(docx|xlsx)$/i);

  const handleAction = async (action: string) => {
    setSelectedFile(file.id);

    switch (action) {
      case 'select':
        // Enable selection mode if not already enabled
        if (!isSelectionMode) {
          toggleSelectionMode();
        }
        // Select the current file
        toggleFileSelection(file.id);
        break;
      case 'download':
        try {
          const result = await generateAccessUrl({
            fileIds: [file.id],
            expirySeconds: 3600, // 1 hour
          });

          const accessUrl = result.filesGenerateFileAccessURL?.results?.[0];
          if (accessUrl?.url) {
            // Open download URL in new tab
            window.open(accessUrl.url, '_blank');
            toast.success(
              t.fileActions.downloadingFile.replace('{name}', file.name),
            );
          } else {
            toast.error(accessUrl?.error || t.fileActions.failedToDownload);
          }
        } catch (error) {
          console.error('Failed to download file:', error);
          toast.error(t.fileActions.failedToDownload);
        }
        break;
      case 'rename':
        setShowRename(true);
        break;
      case 'move':
        setShowMove(true);
        break;
      case 'delete':
        setShowDelete(true);
        break;
      case 'template':
        setTemplateDialogFileId(file.id);
        break;
    }
  };

  const menuItems = [
    // Only show "Select" option when not in selection mode
    ...(!isSelectionMode
      ? [
          {
            label: (
              <div className="flex items-center gap-2">
                <CheckSquare className="size-4" />
                {t.fileActions.select}
              </div>
            ),
            onAction: () => handleAction('select'),
          },
          'separator' as const,
        ]
      : []),
    ...(!isFolder
      ? [
          {
            label: (
              <div className="flex items-center gap-2">
                <Download className="size-4" />
                {t.fileActions.download}
              </div>
            ),
            onAction: () => handleAction('download'),
          },
        ]
      : []),
    ...(canRenameFile(file)
      ? [
          {
            label: (
              <div className="flex items-center gap-2">
                <Edit className="size-4" />
                {t.fileActions.rename}
              </div>
            ),
            onAction: () => handleAction('rename'),
          },
        ]
      : []),
    ...(canMoveFile(file)
      ? [
          {
            label: (
              <div className="flex items-center gap-2">
                <Move className="size-4" />
                {t.fileActions.moveTo}
              </div>
            ),
            onAction: () => handleAction('move'),
          },
        ]
      : []),
    ...((canRenameFile(file) || canMoveFile(file)) &&
    (isOfficeDoc || canDeleteFile(file))
      ? ['separator' as const]
      : []),
    ...(isOfficeDoc
      ? [
          {
            label: (
              <div className="flex items-center gap-2">
                <FileType2 className="size-4" />
                {t.fileActions.setAsTemplate}
              </div>
            ),
            onAction: () => handleAction('template'),
          },
          'separator' as const,
        ]
      : []),
    ...(canDeleteFile(file)
      ? [
          {
            label: (
              <div className="flex items-center gap-2 text-failure">
                <Trash2 className="size-4" />
                {t.fileActions.delete}
              </div>
            ),
            onAction: () => handleAction('delete'),
          },
        ]
      : []),
  ];

  return (
    <div className="@FileActionMenu">
      <Menu
        trigger={
          <Button size="icon" icon={<MoreHorizontal />} variant="outline" />
        }
        items={menuItems}
        placement="bottom end"
      />
    </div>
  );
};
